import { useCallback, useEffect, useState } from 'react';

import type { FundPolicy } from '../../../core/fundPolicy/schema';
import {
  EMPTY_DEPENDENT_FORM,
  countByRelation,
  toNewDependent,
  validateDependentForm,
  type DependentFieldErrors,
  type DependentFormValues,
} from './dependentForm';
import type { DependentPort, MyDependent } from './ports/dependentPort';

/**
 * Loads the employee's dependants and evaluates a candidate against the stored
 * policy as they type (§5.4 — coordination only; the rules live in
 * `dependentForm.ts`).
 */
export interface MyDependentsController {
  readonly dependents: readonly MyDependent[];
  readonly errors: DependentFieldErrors;
  readonly ineligibleReasonsAr: readonly string[];
  readonly requiresProofDocument: boolean;
  readonly canAddMore: boolean;
  readonly errorAr: string | null;
  readonly validate: (values: DependentFormValues) => void;
  readonly add: (values: DependentFormValues) => Promise<void>;
  /** Changes one already-entered dependant, while the rules still allow it. */
  readonly update: (id: string, values: DependentFormValues) => Promise<void>;
}

export function useMyDependents(
  dependentsPort: DependentPort,
  policy: FundPolicy | null,
  enabled: boolean,
  today: string,
  /** The employee's own name, which a child's is derived from. */
  employeeFullName: string,
): MyDependentsController {
  const [dependents, setDependents] = useState<readonly MyDependent[]>([]);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [validation, setValidation] = useState<{
    errors: DependentFieldErrors;
    ineligibleReasonsAr: readonly string[];
    requiresProofDocument: boolean;
  }>({ errors: {}, ineligibleReasonsAr: [], requiresProofDocument: false });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    dependentsPort
      .listMyDependents()
      .then((list) => {
        if (!cancelled) setDependents(list);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل بيانات المعالين.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, dependentsPort, reloadToken]);

  const canAddMore = policy !== null && dependents.length < policy.maxDependentsPerEmployee;

  const validate = useCallback(
    (values: DependentFormValues) => {
      if (!policy) return;

      const result = validateDependentForm(values, policy, {
        existingByRelation: countByRelation(dependents),
        existingTotal: dependents.length,
        today,
        employeeFullName,
      });

      setValidation({
        errors: result.errors,
        ineligibleReasonsAr: result.ineligibleReasonsAr,
        requiresProofDocument: result.requiresProofDocument,
      });
    },
    [policy, dependents, today, employeeFullName],
  );

  const update = useCallback(
    async (id: string, values: DependentFormValues) => {
      if (!policy) return;
      setErrorAr(null);

      const result = validateDependentForm(values, policy, {
        // The dependant being edited is excluded from its own caps: counting it
        // would refuse an edit that changes nothing about how many there are.
        existingByRelation: countByRelation(
          dependents.filter((dependent) => dependent.id !== id),
        ),
        existingTotal: dependents.length - 1,
        today,
        employeeFullName,
      });

      if (!result.valid) {
        setValidation({
          errors: result.errors,
          ineligibleReasonsAr: result.ineligibleReasonsAr,
          requiresProofDocument: result.requiresProofDocument,
        });
        return;
      }

      try {
        await dependentsPort.updateDependent(id, toNewDependent(values, employeeFullName));
        setValidation({ errors: {}, ineligibleReasonsAr: [], requiresProofDocument: false });
        setReloadToken((token) => token + 1);
      } catch (error) {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر تعديل بيانات المعال.');
      }
    },
    [policy, dependents, today, employeeFullName, dependentsPort],
  );

  const add = useCallback(
    async (values: DependentFormValues) => {
      if (!policy) return;
      setErrorAr(null);

      // Re-validated here, not just on change. The screen disables the button,
      // but a disabled button is a courtesy — this is the check that holds if
      // the form is submitted some other way.
      const result = validateDependentForm(values, policy, {
        existingByRelation: countByRelation(dependents),
        existingTotal: dependents.length,
        today,
        employeeFullName,
      });

      if (!result.valid) {
        setValidation({
          errors: result.errors,
          ineligibleReasonsAr: result.ineligibleReasonsAr,
          requiresProofDocument: result.requiresProofDocument,
        });
        return;
      }

      try {
        await dependentsPort.addDependent(toNewDependent(values, employeeFullName));
        setValidation({ errors: {}, ineligibleReasonsAr: [], requiresProofDocument: false });
        setReloadToken((token) => token + 1);
      } catch (error) {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر إضافة المعال.');
      }
    },
    [policy, dependents, today, employeeFullName, dependentsPort],
  );

  return {
    dependents,
    errors: validation.errors,
    ineligibleReasonsAr: validation.ineligibleReasonsAr,
    requiresProofDocument: validation.requiresProofDocument,
    canAddMore,
    errorAr,
    validate,
    add,
    update,
  };
}

export { EMPTY_DEPENDENT_FORM };
